import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  BookMarked,
  ChevronLeft,
  ChevronRight,
  ClipboardCheck,
  FileText,
  HelpCircle,
  Map,
  Plus,
  Search,
  Shield,
  type LucideIcon,
} from "lucide-react";
import {
  ARTICLE_TYPE_LABEL,
  createCategory,
  listArticles,
  listCategories,
  searchKb,
  type ArticleListItem,
  type ArticleType,
  type CategoryNode,
  type SearchHit,
} from "../api/knowledge";
import { ApiError } from "../api/client";
import { useCurrentUser } from "../hooks/useCurrentUser";

function canEdit(roles: string[]): boolean {
  return roles.some(
    (r) => r === "super_admin" || r === "admin" || r === "content_manager",
  );
}

// Подбор иконки по slug (с фолбэком). Без модельного поля.
const CATEGORY_ICONS: Record<string, LucideIcon> = {
  reglament: FileText,
  reglamenty: FileText,
  reglaments: FileText,
  skript: BookMarked,
  skripty: BookMarked,
  scripts: BookMarked,
  cheklist: ClipboardCheck,
  checklist: ClipboardCheck,
  cheklisty: ClipboardCheck,
  safety: Shield,
  bezopasnost: Shield,
  marshrut: Map,
  marshruty: Map,
  routes: Map,
  faq: HelpCircle,
  voprosy: HelpCircle,
};

function iconFor(slug: string): LucideIcon {
  return CATEGORY_ICONS[slug] ?? FileText;
}

export function KnowledgePage() {
  const { user } = useCurrentUser();
  const navigate = useNavigate();
  const [tree, setTree] = useState<CategoryNode[]>([]);
  const [recent, setRecent] = useState<ArticleListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter chip — null = all (categories grid), category_id = articles in that cat
  const [activeCat, setActiveCat] = useState<number | "all">("all");

  // Search
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [searchResults, setSearchResults] = useState<SearchHit[] | null>(null);
  const [searching, setSearching] = useState(false);

  // Admin: inline category create
  const [newCatOpen, setNewCatOpen] = useState(false);
  const [newCatName, setNewCatName] = useState("");
  const [newCatSlug, setNewCatSlug] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [cats, arts] = await Promise.all([
        listCategories(),
        listArticles({
          status: canEdit(user?.roles ?? []) ? undefined : "published",
        }),
      ]);
      setTree(cats.items);
      setRecent(arts.items);
    } catch (err) {
      setError(err instanceof ApiError ? `Ошибка ${err.status}` : "Сеть недоступна");
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    void load();
  }, [load]);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    if (!debouncedQ) {
      setSearchResults(null);
      return;
    }
    setSearching(true);
    void searchKb(debouncedQ)
      .then((r) => setSearchResults(r.items))
      .catch(() => setSearchResults([]))
      .finally(() => setSearching(false));
  }, [debouncedQ]);

  const topCategories = useMemo(() => tree, [tree]);

  const filteredArticles = useMemo(() => {
    if (activeCat === "all") return recent;
    return recent.filter((a) => a.category_id === activeCat);
  }, [recent, activeCat]);

  if (!user) return null;
  const editable = canEdit(user.roles);

  async function handleCreateCategory(e: FormEvent) {
    e.preventDefault();
    try {
      await createCategory({
        name: newCatName.trim(),
        slug: newCatSlug.trim(),
      });
      setNewCatName("");
      setNewCatSlug("");
      setNewCatOpen(false);
      await load();
    } catch (err) {
      alert(err instanceof ApiError ? `Ошибка ${err.status}` : "Не удалось");
    }
  }

  const showingSearch = searchResults !== null && debouncedQ.length > 0;

  return (
    <div className="min-h-full bg-surface pb-20 md:pb-8">
      <header className="sticky top-0 z-10 border-b border-line bg-surface/95 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center px-4 py-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="grid h-9 w-9 place-items-center rounded-lg text-ink-soft transition hover:bg-surface-alt"
            aria-label="Назад"
          >
            <ChevronLeft size={20} strokeWidth={2} />
          </button>
          <h1 className="flex-1 text-center text-base font-semibold text-ink">
            База знаний
          </h1>
          {editable ? (
            <button
              type="button"
              onClick={() => navigate("/kb/articles/new")}
              className="grid h-9 w-9 place-items-center rounded-lg text-brand transition hover:bg-surface-alt"
              aria-label="Новая статья"
            >
              <Plus size={20} strokeWidth={2} />
            </button>
          ) : (
            <span className="h-9 w-9" aria-hidden />
          )}
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 pt-4">
        {/* Search */}
        <div className="relative mb-4">
          <Search
            size={16}
            strokeWidth={2}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted"
          />
          <input
            type="search"
            placeholder="Поиск по материалам"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-full rounded-xl border border-line bg-white py-2.5 pl-10 pr-3 text-sm placeholder:text-ink-muted focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
          />
        </div>

        {error && (
          <div role="alert" className="mb-3 rounded bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        {showingSearch ? (
          <SearchResults
            results={searchResults ?? []}
            searching={searching}
            query={debouncedQ}
          />
        ) : (
          <>
            {/* Category chips */}
            <div className="-mx-1 mb-4 flex gap-1.5 overflow-x-auto pb-1">
              <ChipButton
                active={activeCat === "all"}
                onClick={() => setActiveCat("all")}
                label="Все"
              />
              {topCategories.map((c) => (
                <ChipButton
                  key={c.id}
                  active={activeCat === c.id}
                  onClick={() => setActiveCat(c.id)}
                  label={c.name}
                />
              ))}
            </div>

            {/* Admin: create category */}
            {editable && (
              <div className="mb-4">
                {newCatOpen ? (
                  <form
                    onSubmit={handleCreateCategory}
                    className="flex flex-wrap gap-2 rounded-xl bg-white p-3 ring-1 ring-line"
                  >
                    <input
                      type="text"
                      required
                      placeholder="Название категории"
                      value={newCatName}
                      onChange={(e) => setNewCatName(e.target.value)}
                      className="input flex-1 min-w-[180px]"
                    />
                    <input
                      type="text"
                      required
                      pattern="[a-z0-9\-]+"
                      placeholder="slug-latin"
                      value={newCatSlug}
                      onChange={(e) => setNewCatSlug(e.target.value)}
                      className="input w-40 font-mono text-xs"
                    />
                    <button type="submit" className="btn-primary">
                      Создать
                    </button>
                    <button
                      type="button"
                      onClick={() => setNewCatOpen(false)}
                      className="btn-secondary"
                    >
                      Отмена
                    </button>
                  </form>
                ) : (
                  <button
                    type="button"
                    onClick={() => setNewCatOpen(true)}
                    className="text-sm font-medium text-brand hover:underline"
                  >
                    + Новая категория
                  </button>
                )}
              </div>
            )}

            {loading ? (
              <ListSkeleton />
            ) : activeCat === "all" ? (
              <CategoriesGrid categories={topCategories} />
            ) : (
              <ArticlesList articles={filteredArticles} />
            )}
          </>
        )}
      </main>
    </div>
  );
}

function ChipButton({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`shrink-0 whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm transition ${
        active
          ? "bg-brand text-white"
          : "bg-surface-alt text-ink-soft hover:bg-line"
      }`}
    >
      {label}
    </button>
  );
}

function CategoriesGrid({ categories }: { categories: CategoryNode[] }) {
  if (categories.length === 0) {
    return (
      <div className="card p-8 text-center text-sm text-ink-muted">
        Категорий пока нет.
      </div>
    );
  }
  return (
    <ul className="space-y-2">
      {categories.map((c) => {
        const Icon = iconFor(c.slug);
        return (
          <li key={c.id}>
            <Link
              to={`/kb?category_id=${c.id}`}
              className="group flex items-center gap-3 rounded-xl bg-white px-4 py-3.5 ring-1 ring-line transition hover:shadow-sm"
            >
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-brand/10 text-brand">
                <Icon size={18} strokeWidth={2} />
              </span>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-ink">{c.name}</div>
                <div className="mt-0.5 text-xs text-ink-muted">
                  {c.articles_count}{" "}
                  {pluralizeDocs(c.articles_count)}
                </div>
              </div>
              <ChevronRight
                size={16}
                strokeWidth={2}
                className="shrink-0 text-ink-muted"
              />
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

function ArticlesList({ articles }: { articles: ArticleListItem[] }) {
  if (articles.length === 0) {
    return (
      <div className="card p-8 text-center text-sm text-ink-muted">
        В этой категории пока нет статей.
      </div>
    );
  }
  return (
    <ul className="space-y-2">
      {articles.map((a) => (
        <li key={a.id}>
          <Link
            to={`/kb/articles/${a.id}`}
            className="flex items-center gap-3 rounded-xl bg-white px-4 py-3 ring-1 ring-line transition hover:shadow-sm"
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="truncate text-sm font-medium text-ink">{a.title}</span>
                {a.article_type !== "article" && (
                  <span className={`badge ${typeBadgeClass(a.article_type)} shrink-0`}>
                    {ARTICLE_TYPE_LABEL[a.article_type]}
                  </span>
                )}
              </div>
              <div className="mt-0.5 text-[11px] text-ink-muted">
                {a.category_name ?? "Без категории"}
                {a.status !== "published" && (
                  <span className="ml-1 rounded bg-surface-alt px-1.5 py-0.5 text-[10px] uppercase">
                    {a.status}
                  </span>
                )}
              </div>
            </div>
            <ChevronRight
              size={16}
              strokeWidth={2}
              className="shrink-0 text-ink-muted"
            />
          </Link>
        </li>
      ))}
    </ul>
  );
}

function typeBadgeClass(t: ArticleType): string {
  switch (t) {
    case "regulation":
      return "bg-adm-importantBg text-brand-ochre";
    case "script":
      return "bg-adm-scriptBg text-[#3d6e80]";
    case "checklist":
      return "bg-adm-tipBg text-brand-success";
    default:
      return "badge-neutral";
  }
}

function SearchResults({
  results,
  searching,
  query,
}: {
  results: SearchHit[];
  searching: boolean;
  query: string;
}) {
  return (
    <section>
      <div className="mb-3 text-xs uppercase tracking-wide text-ink-muted">
        Найдено: {results.length} {searching && "…"}
      </div>
      {results.length === 0 ? (
        <div className="card p-8 text-center text-sm text-ink-muted">
          По «{query}» ничего не найдено.
        </div>
      ) : (
        <ul className="space-y-2">
          {results.map((h) => (
            <li key={h.id}>
              <Link
                to={`/kb/articles/${h.id}`}
                className="block rounded-xl bg-white p-4 ring-1 ring-line transition hover:shadow-sm"
              >
                <div className="text-sm font-medium text-ink">{h.title}</div>
                {h.category_name && (
                  <div className="mt-0.5 text-[11px] text-ink-muted">
                    {h.category_name}
                  </div>
                )}
                <div
                  className="kb-snippet mt-1 text-sm text-ink-soft"
                  dangerouslySetInnerHTML={{ __html: h.snippet }}
                />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function ListSkeleton() {
  return (
    <ul className="space-y-2">
      {[0, 1, 2, 3, 4].map((i) => (
        <li key={i} className="h-16 animate-pulse rounded-xl bg-surface-alt" />
      ))}
    </ul>
  );
}

function pluralizeDocs(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return "документ";
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return "документа";
  return "документов";
}
